// hooks/usePayrollAuth.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCheckPayrollAuth, postCheckPayrollAuth, updateLockedSectionsAPI } from '../api/auth';
import { getItemWithExpiration } from '../lib/utils';
import { useEffect, useState } from 'react';
import { usePayrollAuthContext } from '../context/PayrollAuthContext';
import {
    BROWSER_DECISION_KEYS,
    BrowserDecisionKey,
    readClientLockDecisions,
} from '../lib/lockedSections';
export function usePayrollAuth() {
    const authObject = getItemWithExpiration('authObject');
    const authorization = getItemWithExpiration('authorization');
    const isCenterViewer = authObject?.accessMode === 'view' || authObject?.role === 'center_viewer';
    const queryClient = useQueryClient();
    const { reportLockFetch, lockSupport, claimLockMigration } = usePayrollAuthContext();
    const accountId = authObject?.adminId ? String(authObject.adminId) : null;
    const enabled =
        !!authObject &&
        !!authorization &&
        !!authObject.adminId &&
        authObject.role !== 'superadmin' &&
        !isCenterViewer;

    const { data, isLoading, isError, isSuccess, dataUpdatedAt } = useQuery({
        /*
         * 계정 id 를 키에 넣는다. 같은 PC 에서 계정을 바꾸면 A 의 신선한 캐시가
         * B 에게 그대로 재사용되어, B 의 잠금 설정이 A 의 것으로 보인다.
         * (앞부분이 같아서 기존 invalidateQueries(['getCheckPayrollAuthQuery']) 는 그대로 동작한다)
         */
        queryKey: ['getCheckPayrollAuthQuery', authObject?.adminId ?? null],
        queryFn: async () => await getCheckPayrollAuth(authObject.adminId, authorization),
        enabled,
        /*
         * 이 쿼리만 창으로 돌아올 때마다 다시 받는다(전역 기본값은 false·staleTime 1분 — 건드리지 않는다).
         * 관리자 앱이나 다른 PC 에서 바꾼 잠금(손실 시그널·기프티콘 포함)이 다시 보일 때 반영된다.
         */
        refetchOnWindowFocus: 'always',
    });

    const [isSettingPayrolPassword, setIsSettingPayrolPassword] = useState<boolean>(
        data?.data?.isSettingPayrolPassword
    );

    useEffect(() => {
        setIsSettingPayrolPassword(data?.data?.isSettingPayrolPassword);
        /*
         * 잠금 계약 판정을 매 응답마다 다시 한다(캐시된 v3 를 믿지 않는다).
         * dataUpdatedAt 을 함께 보는 이유: 같은 내용으로 다시 받으면 data 참조가 그대로라
         * (structural sharing) 응답이 왔다는 사실을 놓친다.
         */
        if (!enabled) {
            reportLockFetch(accountId, { kind: 'inactive' });
        } else if (isError) {
            reportLockFetch(accountId, { kind: 'error' });
        } else if (isSuccess) {
            reportLockFetch(accountId, { kind: 'data', body: data?.data });
        }
    }, [data, dataUpdatedAt, isError, isSuccess, enabled, accountId, reportLockFetch]);

    /*
     * 브라우저 결정 이관(1회) — 설계 §10-3.
     *
     * 서버가 새 계약(v3)인데 이 계정의 서버 결정이 미정이고, 이 계정 브라우저 칸에 결정이 있으면
     * 그 키마다 `lockDecisionsOnlyIfUnset` 으로 한 번 보낸다. 다른 PC 가 먼저 정했으면 서버 값이 이긴다.
     *  · 키는 **하나씩 차례로** 보낸다(동시에 보내면 응답 순서가 뒤집힐 수 있다).
     *  · 이관 응답 본문은 상태에 반영하지 않는다. 하나라도 서버가 처리했으면(applied true/false)
     *    끝난 뒤 잠금 조회를 다시 해서 **그 응답으로만** 반영한다. 반영 전에 다른 화면이 이관 전 캐시를
     *    다시 적용해도, 브라우저 결정이 남아 있어 잠금이 풀리지 않는다.
     *  · 브라우저 결정은 여기서 지우지 않는다 — 잠금 조회 응답이 서버 결정을 확인했을 때
     *    provider(reportLockFetch)가 지운다. 실패하면 그대로 남아 다음 진입에 다시 보낸다.
     *  · 키당 1회 표식은 provider 가 가진다(이 훅은 여러 화면에 동시에 떠 있다).
     */
    useEffect(() => {
        if (lockSupport.state !== 'v3' || !lockSupport.accountId) return;
        const account = lockSupport.accountId;
        if (account !== accountId || !authorization) return;
        const browser = readClientLockDecisions(account);
        const targets: Array<{ key: BrowserDecisionKey; value: boolean }> = [];
        // 이관 대상은 브라우저에 결정을 기록하던 두 키뿐(스케줄 동작 키는 브라우저 이력이 없다)
        BROWSER_DECISION_KEYS.forEach((key) => {
            if (lockSupport.decisions[key] !== null) return;
            const value = browser[key];
            if (typeof value !== 'boolean') return;
            if (!claimLockMigration(account, key)) return;
            targets.push({ key, value });
        });
        if (targets.length === 0) return;

        (async () => {
            let handled = false;
            for (const { key, value } of targets) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const res: any = await updateLockedSectionsAPI(account, undefined, authorization, {
                        contract: true,
                        lockDecisions: { [key]: value },
                        lockDecisionsOnlyIfUnset: true,
                        silent: true,
                    });
                    if (res?.status === 200 && typeof res?.data?.applied === 'boolean') handled = true;
                } catch {
                    // 남겨 둔다 — 다음 진입에 다시 이관한다
                }
            }
            if (handled) queryClient.invalidateQueries(['getCheckPayrollAuthQuery']);
        })();
    }, [lockSupport, accountId, authorization, claimLockMigration, queryClient]);
    const mutation = useMutation(
        (password: string) => postCheckPayrollAuth(authObject.adminId, password, authorization),
        {
            onSuccess({ status }) {
                switch (status) {
                    case 200:
                        return;
                    case 201:
                        alert('비밀번호가 설정되었습니다.');
                        queryClient.invalidateQueries(['getCheckPayrollAuthQuery']);
                        setIsSettingPayrolPassword(true);
                        return;
                    case 415:
                        alert('비밀번호가 틀렸습니다.');
                        return;
                }
            },
        }
    );

    return {
        isLoading,
        isPasswordSet: isSettingPayrolPassword,
        verifyPassword: (pw: string) => mutation.mutateAsync(pw),
        setPassword: (pw: string) => mutation.mutateAsync(pw),
    };
}
